import React, { useState } from 'react';
import styled from 'styled-components';
import {
    EuiFlyout,
    EuiFlyoutBody,
    EuiFlyoutHeader,
    EuiTitle,
    EuiIcon
} from '@elastic/eui';
import utilsService from '../../../../../servises/utils.service';

const ShowDrilldownStyled = styled.div`
  color: #1890ff;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  line-height: 16px;

  &:hover {
    text-decoration: underline;
  }

  svg {
    path {
      fill: #1890ff;
    }
  }
`;


const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  margin-top: 10px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.div`
  grid-column: span 2;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--color-primary, #6366f1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 6px;
  margin-top: 14px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const CpuHeroSection = styled.div`
  grid-column: span 2;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    grid-column: span 1;
    flex-direction: column;
    text-align: center;
  }

  .brand-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 60%;
    
    @media (max-width: 600px) {
      max-width: 100%;
    }

    .brand-header {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: ${props => props.brandColor || '#9f7aea'};
      
      @media (max-width: 600px) {
        justify-content: center;
      }
    }

    .cpu-model-name {
      font-size: 18px;
      font-weight: 800;
      color: #fff;
      line-height: 1.3;
    }
  }

  .gauge-group {
    display: flex;
    align-items: center;
    gap: 16px;
  }
`;

const StatItem = styled.div`
  background: rgba(255, 255, 255, 0.01);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  
  .label {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.45);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    font-family: Consolas, monospace;
    word-break: break-all;
  }
`;

const LargeGauge = ({ value, color }) => {
    const radius = 36;
    const stroke = 6;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;

    return (
        <div style={{ position: 'relative', width: radius * 2, height: radius * 2 }}>
            <svg height={radius * 2} width={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
                <circle
                    stroke="rgba(255,255,255,0.06)"
                    fill="transparent"
                    strokeWidth={stroke}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                />
                <circle
                    stroke={color}
                    fill="transparent"
                    strokeWidth={stroke}
                    strokeDasharray={circumference + ' ' + circumference}
                    style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease' }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                />
            </svg>
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                fontFamily: 'Consolas, monospace'
            }}>
                <span style={{ fontSize: '15px', fontWeight: 800, color: '#fff' }}>{value}%</span>
                <span style={{ fontSize: '7px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: 600 }}>Load</span>
            </div>
        </div>
    );
};

const getCpuBrandStyle = cpuName => {
    const name = (cpuName || "").toLowerCase();
    if (name.includes("intel")) {
        return {
            color: "#0071C5",
            bg: "rgba(0, 113, 197, 0.15)",
            displayName: "Intel® Core™",
            icon: "logoTerminal"
        };
    }
    if (name.includes("amd") || name.includes("ryzen")) {
        return {
            color: "#ED1C24",
            bg: "rgba(237, 28, 36, 0.15)",
            displayName: "AMD® Ryzen™",
            icon: "logoTerminal"
        };
    }
    if (name.includes("apple") || name.includes("m1") || name.includes("m2") || name.includes("m3")) {
        return {
            color: "#ffffff",
            bg: "rgba(255, 255, 255, 0.1)",
            displayName: "Apple Silicon",
            icon: "logoMac"
        };
    }
    return {
        color: "#9f7aea",
        bg: "rgba(159, 122, 234, 0.1)",
        displayName: "Processor",
        icon: "compute"
    };
};

const CpuDrilldown = ({ server }) => {
    const [showFlyout, setShowFlyout] = useState(false);

    const cpuName = utilsService.getFromMetrics(server, 'cpu_name', 'Generic CPU');
    const cpuFreq = utilsService.getFromMetrics(server, 'cpu_freq', '-');
    const cpuCores = utilsService.getFromMetrics(server, 'cpu_cores', '-');
    const cpuLoad = parseInt(utilsService.getFromMetrics(server, 'load_cpu', 0), 10);
    const loadAvg = utilsService.getFromMetrics(server, 'load', '-');
    const loadIo = utilsService.getFromMetrics(server, 'load_io', '-');

    const brandStyle = getCpuBrandStyle(cpuName);
    const cpuDetails = utilsService.getFromMetrics(server, 'cpu_details', null);

    let loadColor = "rgb(0, 128, 213)";
    if (cpuLoad > 70) loadColor = "rgb(249, 190, 73)";
    if (cpuLoad > 90) loadColor = "rgb(248, 107, 99)";

    return (
        <>
            <ShowDrilldownStyled onClick={ () => setShowFlyout(true) }>
                Details
                <EuiIcon type="inspect" size="s" style={{ alignSelf: 'center' }} />
            </ShowDrilldownStyled>

            { showFlyout && (
                <EuiFlyout
                    size="l"
                    ownFocus={true}
                    outsideClickCloses={true}
                    onClose={ () => setShowFlyout(false) }
                >
                    <EuiFlyoutHeader hasBorder>
                        <EuiTitle size="m">
                            <h2>Detailed CPU Dashboard</h2>
                        </EuiTitle>
                    </EuiFlyoutHeader>
                    <EuiFlyoutBody>
                        <CpuHeroSection brandColor={brandStyle.color}>
                            <div className="brand-group">
                                <div className="brand-header">
                                    <EuiIcon type={brandStyle.icon || "compute"} size="s" />
                                    <span>{brandStyle.displayName}</span>
                                </div>
                                <div className="cpu-model-name">{cpuName}</div>
                            </div>
                            <div className="gauge-group">
                                <LargeGauge value={cpuLoad} color={loadColor} />
                            </div>
                        </CpuHeroSection>

                        <ContentGrid>
                            <SectionHeader>
                                <EuiIcon type="compute" size="s" /> Real-time Performance
                            </SectionHeader>
                            <StatItem>
                                <span className="label">Current Utilization</span>
                                <span className="value" style={{ color: loadColor }}>{cpuLoad}%</span>
                            </StatItem>
                            <StatItem>
                                <span className="label">Clock Frequency</span>
                                <span className="value">{cpuFreq}</span>
                            </StatItem>
                            <StatItem>
                                <span className="label">Load Average</span>
                                <span className="value">{loadAvg}</span>
                            </StatItem>
                            <StatItem>
                                <span className="label">I/O Wait Load</span>
                                <span className="value">{loadIo !== '-' ? `${loadIo}%` : '-'}</span>
                            </StatItem>

                            <SectionHeader>
                                <EuiIcon type="indexSettings" size="s" /> Core Topography
                            </SectionHeader>
                            <StatItem>
                                <span className="label">CPU Cores</span>
                                <span className="value">{cpuCores} Cores</span>
                            </StatItem>
                            {cpuDetails?.threads && (
                                <StatItem>
                                    <span className="label">Logical Threads</span>
                                    <span className="value">{cpuDetails.threads} Threads</span>
                                </StatItem>
                            )}
                            {cpuDetails?.sockets && (
                                <StatItem>
                                    <span className="label">Physical Sockets</span>
                                    <span className="value">{cpuDetails.sockets} Sockets</span>
                                </StatItem>
                            )}

                            {cpuDetails && (
                                <>
                                    <SectionHeader>
                                        <EuiIcon type="inspect" size="s" /> Processor Specifications
                                    </SectionHeader>
                                    {cpuDetails.arch && (
                                        <StatItem>
                                            <span className="label">Architecture</span>
                                            <span className="value">{cpuDetails.arch}</span>
                                        </StatItem>
                                    )}
                                    {cpuDetails.vendor && (
                                        <StatItem>
                                            <span className="label">Vendor</span>
                                            <span className="value">{cpuDetails.vendor}</span>
                                        </StatItem>
                                    )}
                                    {cpuDetails.hardware && (
                                        <StatItem>
                                            <span className="label">Hardware ID</span>
                                            <span className="value">{cpuDetails.hardware}</span>
                                        </StatItem>
                                    )}
                                    {cpuDetails.implementer && (
                                        <StatItem>
                                            <span className="label">Implementer</span>
                                            <span className="value">{cpuDetails.implementer}</span>
                                        </StatItem>
                                    )}
                                    {cpuDetails.part && (
                                        <StatItem>
                                            <span className="label">Part</span>
                                            <span className="value">{cpuDetails.part}</span>
                                        </StatItem>
                                    )}
                                    {cpuDetails.revision && (
                                        <StatItem>
                                            <span className="label">Revision</span>
                                            <span className="value">{cpuDetails.revision}</span>
                                        </StatItem>
                                    )}
                                    {cpuDetails.model && (
                                        <StatItem style={{ gridColumn: 'span 2' }}>
                                            <span className="label">Model Code</span>
                                            <span className="value">{cpuDetails.model}</span>
                                        </StatItem>
                                    )}
                                    {(cpuDetails.min_mhz || cpuDetails.max_mhz) && (
                                        <StatItem style={{ gridColumn: 'span 2' }}>
                                            <span className="label">Core Clock Range</span>
                                            <span className="value">
                                                {cpuDetails.min_mhz ? `${cpuDetails.min_mhz} MHz` : 'N/A'} - {cpuDetails.max_mhz ? `${cpuDetails.max_mhz} MHz` : 'N/A'}
                                            </span>
                                        </StatItem>
                                    )}
                                </>
                            )}
                        </ContentGrid>
                    </EuiFlyoutBody>
                </EuiFlyout>
                ) }
        </>
    );
};

export default CpuDrilldown;
